import {Api, ApiError} from "modules/utils";
import {call, delay, put, select} from "typed-redux-saga";
import * as actions from "modules/actions";
import {get, map, eq, uniqBy, size, toArray} from "lodash";
import {getUserId, getLeaguesForJoin, getLeagueLadder} from "modules/selectors";
import {
	IEmailInvites,
	IFetchLeague,
	ILeagueLadderPayload,
	ILeagueCreate,
	IPostFetchLeaguesForJoin,
	IPostLeagueJoin,
	IPostLeagueLeave,
	ISagaAction,
} from "modules/types";

export const fetchMyLeaguesSaga = function* () {
	try {
		const response = yield* call(Api.League.show_my);

		ApiError.CHECK(response);

		const leagues = get(response, "result.leagues", []);

		const current_user_id = yield* select(getUserId);

		const formatted_leagues = map(leagues, (league) => ({
			...league,
			is_commissioner: eq(current_user_id, league.user_id),
		}));

		yield* put(actions.fetchMyLeaguesSuccess(formatted_leagues));
	} catch (e) {
		yield* put(actions.fetchMyLeaguesFailed(e as Error));
	}
};

export const postLeagueCreateSaga = function* (action: ISagaAction<ILeagueCreate>) {
	try {
		const response = yield* call(Api.League.create, action.payload);
		ApiError.CHECK(response);

		yield* put(actions.postLeagueCreateSuccess(response.result));
	} catch (e) {
		yield* put(actions.postLeagueCreateFailed(e as Error));
	}
};

export const postLeagueUpdateSaga = function* (action: ISagaAction<ILeagueCreate>) {
	try {
		const response = yield* call(Api.League.update, action.payload);
		ApiError.CHECK(response);

		yield* put(
			actions.postLeagueUpdateSuccess({
				updated: true,
				league: response.result,
			})
		);
	} catch (e) {
		yield* put(actions.postLeagueUpdateFailed(e as Error));
	}
};

export const postInvitesSendViaEmailSaga = function* (action: ISagaAction<IEmailInvites>) {
	try {
		const response = yield* call(Api.Joins.invite, action.payload);
		ApiError.CHECK(response);

		yield* put(actions.inviteViaEmailSuccess());
		yield* delay(3000);
		yield* put(actions.inviteViaEmailClear());
	} catch (e) {
		yield* put(
			actions.showGlobalError({
				text: get(e, "message", ""),
			})
		);
		yield* put(actions.inviteViaEmailFailed(e as Error));
	}
};

export const fetchLeagueSaga = function* (action: ISagaAction<IFetchLeague>) {
	try {
		const response = yield* call(Api.League.show, action.payload);
		ApiError.CHECK(response);

		yield* put(actions.fetchLeagueSuccess({league: response.result}));
	} catch (e) {
		yield* put(actions.fetchLeagueFailed(e as Error));
	}
};

export const postLeagueLeaveSaga = function* (action: ISagaAction<IPostLeagueLeave>) {
	try {
		const response = yield* call(Api.Joins.leave, action.payload);
		ApiError.CHECK(response);

		yield* put(
			actions.postLeagueLeaveSuccess({
				[action.payload.league_id]: {
					joined: false,
					num_teams: get(response, "result.num_teams"),
				},
			})
		);
	} catch (e) {
		yield* put(actions.postLeagueLeaveFailed(e as Error));
	}
};

export const fetchLeaguesForJoinSaga = function* (action: ISagaAction<IPostFetchLeaguesForJoin>) {
	try {
		const response = yield* call(Api.Joins.show, action.payload);
		ApiError.CHECK(response);
		const fetched_leagues = yield* select(getLeaguesForJoin);

		if (size(response.result.leagues) && action.payload.offset) {
			response.result.leagues = uniqBy(
				[...fetched_leagues, ...response.result.leagues],
				"id"
			);
		}

		yield* put(actions.fetchLeaguesForJoinSuccess(response.result));
	} catch (e) {
		yield* put(actions.fetchLeaguesForJoinFailed(e as Error));
	}
};

export const postLeagueJoinSaga = function* (action: ISagaAction<IPostLeagueJoin>) {
	try {
		const response = yield* call(Api.Joins.join, action.payload);
		ApiError.CHECK(response);

		yield* put(
			actions.postLeagueJoinSuccess({
				[action.payload.league_id]: {
					joined: true,
					num_teams: get(response, "result.num_teams"),
				},
			})
		);
	} catch (e) {
		yield* put(
			actions.showGlobalError({
				text: get(e, "message", ""),
				// code: error_code || 0,
			})
		);
		yield* put(actions.postLeagueJoinFailed(e as Error));
	}
};

export const searchAndJoinSaga = function* (action: ISagaAction<Partial<IPostLeagueJoin>>) {
	try {
		const response = yield* call(Api.Joins.join, action.payload);
		ApiError.CHECK(response);

		yield* put(actions.searchAndJoinSuccess(get(response, "result.id")));
	} catch (e) {
		yield* put(
			actions.showGlobalError({
				text: get(e, "message", ""),
				// code: error_code || 0,
			})
		);
		yield* put(actions.postLeagueJoinFailed(e as Error));
	}
};

export const fetchLeagueLadderSaga = function* (action: ISagaAction<ILeagueLadderPayload>) {
	try {
		const params = {
			...action.payload,
			order: action.payload.event_id ? "event_rank" : "overall_rank",
		};

		const response = yield* call(Api.League.ladder, params);
		ApiError.CHECK(response);
		const old_ladder = yield* select(getLeagueLadder);
		const new_ladder = toArray(response.result.ladder);

		const ladder = [...old_ladder, ...new_ladder];

		yield* put(
			actions.fetchLeagueLadderSuccess({
				ladder,
				next: response.result.next,
			})
		);
	} catch (e) {
		yield* put(actions.fetchLeagueLadderFailed(e as Error));
	}
};
export const sortLeagueLadderSaga = function* (action: ISagaAction<ILeagueLadderPayload>) {
	try {
		const params = {
			...action.payload,
			order: action.payload.event_id ? "event_rank" : "overall_rank",
		};
		const response = yield* call(Api.League.ladder, params);
		ApiError.CHECK(response);
		const new_ladder = toArray(response.result.ladder);
		yield* put(
			actions.fetchLeagueLadderSuccess({
				ladder: new_ladder,
				next: response.result.next,
			})
		);
	} catch (e) {
		yield* put(actions.fetchLeagueLadderFailed(e as Error));
	}
};
